import React from 'react';
import { memoize, noop, toLower } from 'lodash';
import Modal from 'ds/components/modal';
import { Text, H3 } from 'ds/components/typography';
import { ITranslate, useLocale } from 'locale';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import LogoIcon from 'assets/svg/developer/suitcase.svg';
import { ShowAt, useScreenBreakpoint } from 'consts/breakpoints';
import { useField, useForm } from 'react-final-form-hooks';
import { TextField } from 'ds/components/input/TextField';
import RadioGroup from 'ds/components/input/RadioGroup';
import { SpacingBlock } from 'components/spacing-block';
import { Button } from 'ds/components/button';
import { ContactDeveloperReason, IDeveloperProfile } from 'utils/entities';
import { DeveloperLeadVariables } from 'store/sagas/apiService/types';
import { isNotEmpty, isPhoneNumberValid } from 'utils/formValidationHelpers';
import withEvents from 'analytics/withEvents';
import PhoneButton from 'components/phone-button';
import { SuccessInner } from './SuccessModal';
import * as Styled from './styled';
import { LeadContactTermsAndConditions } from 'components/term-and-conditions/LeadContactTermsAndConditions';


export const genContactReasonOptions = memoize(t => Object.values(ContactDeveloperReason).map(reason => ({
  label: t('developerPage.contactModal.contactReason.options', { value: reason }),
  value: reason,
})));

const validateField = (t: ITranslate) => (phone: string) => {
  if (!isNotEmpty(phone)) {
    return t('developerPage.contactModal.phone.required');
  }
  else if (!isPhoneNumberValid(phone)) {
    return t('developerPage.contactModal.phone.invalid');
  }

  return undefined;
};

interface ContactModalProps {
  developer: IDeveloperProfile;
  onClose: () => void;
  name: string;
  phone: string;
  logoUrl: string;
  initialValues: DeveloperLeadVariables;
  contact: (data: DeveloperLeadVariables) => void;
  submitting: boolean;
  submitted: boolean;
  onPhoneNumberShow?: (isVisible: boolean) => void;
  onInputClickEvent?: (eventName: string) => void;
}

const ContactModal: React.FC<ContactModalProps> = (props) => {
  const { onClose, name, phone, logoUrl, initialValues, contact, submitting, submitted, onPhoneNumberShow, onInputClickEvent } = props;
  const isMobile = useScreenBreakpoint() === 1;
  const constructImageURI = useImageUrlBuilder();
  const logo = logoUrl ? constructImageURI(logoUrl, { width: 96, fit: 'contain' }) : null;
  const { t } = useLocale();

  const DeveloperName = isMobile ? Text : H3;
  const { form, handleSubmit } = useForm({ onSubmit: noop, initialValues });
  const contactReasonField = useField('contactReason', form);
  const nameField = useField('name', form);
  const phoneField = useField('phone', form, validateField(t));
  const emailField = useField('email', form);

  const { valid, values } = form.getState();
  const onClick = () => {
    if (valid) {
      contact(values);
      if (isMobile) {
        onClose();
      }
    }
  };

  return isMobile ? (
    <Modal
      isOpen
      onClose={onClose}
      title={t('developerPage.contactModal.title')}
      desktopModalPositionCenter
      footerStyle={{
        boxShadow: '0px -4px 10px rgba(0, 0, 0, 0.05)',
        border: 'none',
      }}
    >
      <div style={{ height: 'calc(100vh - 96px)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flexGrow: 1 }}>
          <Styled.InfoWrapper>
            <Styled.LogoImg imageUrl={logo}>
              {logo ? null : <LogoIcon width={48} />}
            </Styled.LogoImg>
            <DeveloperName weight="bold">{name}</DeveloperName>
          </Styled.InfoWrapper>
          {submitted ? (
            <SuccessInner />
          ) : (
            <form onSubmit={handleSubmit}>
              <SpacingBlock mTop={3} mBottom={3}>
                <H3 weight="bold">{t('developerPage.contactModal.contactReason.label')}</H3>
              </SpacingBlock>
              <Styled.ReasonsWrapper>
                <RadioGroup
                  {...contactReasonField.input}
                  {...contactReasonField.meta}
                  options={genContactReasonOptions(t)}
                />
              </Styled.ReasonsWrapper>
              <ShowAt at={1}>
                <Styled.MobileModalSeparator />
              </ShowAt>
              <Styled.ContactInfoWrapper>
                <Styled.ContactInfoInner>
                  <TextField
                    data-auto="name-input"
                    placeholder={t('developerPage.contactModal.name.placeholder')}
                    {...nameField.input}
                    hasError={nameField.meta.dirty && nameField.meta.invalid}
                    fullWidth
                    errorMessage={nameField.meta.error}
                    disabled={submitting}
                  />
                </Styled.ContactInfoInner>
                <Styled.ContactInfoInner>
                  <TextField
                    data-auto="phone-input"
                    placeholder={t('developerPage.contactModal.phone.placeholder')}
                    {...phoneField.input}
                    hasError={phoneField.meta.dirty && phoneField.meta.invalid}
                    type="tel"
                    fullWidth
                    errorMessage={phoneField.meta.error}
                    disabled={submitting}
                    onClick={() => onInputClickEvent('developer_contact_form_phone_text_box_click')}
                  />
                </Styled.ContactInfoInner>
              </Styled.ContactInfoWrapper>
              <TextField
                data-auto="email-input"
                placeholder={t('developerPage.contactModal.email.placeholder')}
                {...emailField.input}
                hasError={emailField.meta.dirty && emailField.meta.invalid}
                fullWidth
                errorMessage={emailField.meta.error}
                disabled={submitting}
              />
              <ShowAt from={2}>
                <SpacingBlock mTop={3}>
                  <Button fullWidth size="extraLarge" onClick={onClick} disabled={submitting || !valid}>
                    {t('developerPage.contactModal.submit')}
                  </Button>
                </SpacingBlock>
              </ShowAt>
              <SpacingBlock mTop={2}>
                <LeadContactTermsAndConditions />
              </SpacingBlock>
            </form>
          )}
        </div>
        <Styled.ButtonsWrapper style={{ flexShrink: 0 }}>
          <Button fullWidth size="extraLarge" onClick={onClick} disabled={submitting || !valid}>
            {t('developerPage.contactModal.submit')}
          </Button>
        </Styled.ButtonsWrapper>
      </div>
    </Modal>
  ) : (
    <Modal
      isOpen
      onClose={onClose}
      title={t('developerPage.contactModal.title')}
      desktopModalPositionCenter
      buttons={(
        <Styled.ButtonsWrapper style={{ flexShrink: 0 }}>
          <Styled.PhoneHint>{t('developerPage.contactModal.phoneHint')}</Styled.PhoneHint>
          <PhoneButton onPhoneNumberShow={onPhoneNumberShow} phone={phone} />
        </Styled.ButtonsWrapper>
      )}
    >
      <Styled.InfoWrapper>
        <Styled.LogoImg imageUrl={logo}>
          {logo ? null : <LogoIcon width={48} />}
        </Styled.LogoImg>
        <DeveloperName weight="bold">{name}</DeveloperName>
      </Styled.InfoWrapper>
      {submitted ? (
        <SuccessInner />
      ) : (
        <form onSubmit={handleSubmit}>
          <SpacingBlock mTop={3} mBottom={3}>
            <H3 weight="bold">{t('developerPage.contactModal.contactReason.label')}</H3>
          </SpacingBlock>
          <Styled.ReasonsWrapper>
            <RadioGroup
              {...contactReasonField.input}
              {...contactReasonField.meta}
              options={genContactReasonOptions(t)}
            />
          </Styled.ReasonsWrapper>
          <ShowAt at={1}>
            <Styled.MobileModalSeparator />
          </ShowAt>
          <Styled.ContactInfoWrapper>
            <Styled.ContactInfoInner>
              <TextField
                data-auto="name-input"
                placeholder={t('developerPage.contactModal.name.placeholder')}
                {...nameField.input}
                hasError={nameField.meta.dirty && nameField.meta.invalid}
                fullWidth
                errorMessage={nameField.meta.error}
                disabled={submitting}
              />
            </Styled.ContactInfoInner>
            <Styled.ContactInfoInner>
              <TextField
                data-auto="phone-input"
                placeholder={t('developerPage.contactModal.phone.placeholder')}
                {...phoneField.input}
                hasError={phoneField.meta.dirty && phoneField.meta.invalid}
                type="tel"
                fullWidth
                errorMessage={phoneField.meta.error}
                disabled={submitting}
                onClick={() => onInputClickEvent('developer_contact_form_phone_text_box_click')}
              />
            </Styled.ContactInfoInner>
          </Styled.ContactInfoWrapper>
          <TextField
            data-auto="email-input"
            placeholder={t('developerPage.contactModal.email.placeholder')}
            {...emailField.input}
            hasError={emailField.meta.dirty && emailField.meta.invalid}
            fullWidth
            errorMessage={emailField.meta.error}
            disabled={submitting}
          />
          <ShowAt from={2}>
            <SpacingBlock mTop={3}>
              <Button fullWidth size="extraLarge" onClick={onClick} disabled={submitting || !valid}>
                {t('developerPage.contactModal.submit')}
              </Button>
            </SpacingBlock>
          </ShowAt>
          <SpacingBlock mTop={1}>
            <LeadContactTermsAndConditions />
          </SpacingBlock>
        </form>
      )}
    </Modal>
  );
};

export default withEvents<ContactModalProps>((sendEvent, props) => {
  const developer = {
    id: props.developer.id,
    integration_level: toLower(props.developer.integrationLevel),
    name: props.developer.name,
  };

  return ({
    onPhoneNumberShow(isVisible: boolean) {
      sendEvent(isVisible ? 'developer_lead_call_submit' : 'developer_contact_form_show_number_click', 'developer', {
        developer,
        ...(isVisible ? {
          rtbData: {
            phone: props.phone,
          },
        } : null),
      });
    },
    onInputClickEvent(eventName: string) {
      sendEvent(eventName, 'developer');
    },
  });
})(ContactModal);
